import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { EnvelopeSimple, MapPin, InstagramLogo, PaperPlaneTilt, DownloadSimple } from "@phosphor-icons/react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLASS =
  "w-full border border-[#E2E8F0] bg-[#FAFAFA] px-4 py-3.5 text-sm font-light text-[#071526] placeholder:text-[#5C6B7C]/60 transition-colors duration-300 focus:border-[#184167] focus:outline-none";

const PARTNER_OPTIONS = [
  "Travel Agency",
  "Tour Operator",
  "DMC",
  "Corporate Travel Company",
  "Independent Travel Advisor",
  "Other",
];

const SOCIALS = [
  {
    label: "English",
    handle: "@wofersglobal",
    href: "https://www.instagram.com/wofersglobal/",
    testId: "contact-instagram-english-link",
  },
  {
    label: "Arabic",
    handle: "@wofers.co",
    href: "https://www.instagram.com/wofers.co/",
    testId: "contact-instagram-arabic-link",
  },
];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", company: "", partner_type: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent — we'll be in touch within one business day.");
      setForm({ name: "", email: "", company: "", partner_type: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your enquiry. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative border-t border-[#E2E8F0] bg-[#F0F4F8] py-24 md:py-36">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-6 md:px-12 lg:grid-cols-2 lg:gap-24">
        <div>
          <Reveal>
            <SectionLabel testId="contact-label">Get in touch</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              data-testid="contact-heading"
              className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-[#071526] md:text-5xl"
            >
              Looking for a trusted local partner in Georgia?{" "}
              <span className="italic text-[#184167]">Let's grow together.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p
              data-testid="contact-copy"
              className="mt-8 max-w-md text-sm font-light leading-relaxed tracking-wide text-[#5C6B7C] md:text-base"
            >
              Tell us about your business and how you'd like to collaborate. We reply to
              every partnership enquiry within one business day.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <a
              href="mailto:wofersglobal@gmail.com"
              data-testid="contact-email-link"
              className="group mt-10 inline-flex items-center gap-4 border-b border-[#184167]/40 pb-3 transition-colors duration-300 hover:border-[#184167]"
            >
              <EnvelopeSimple size={22} weight="thin" className="text-[#184167]" />
              <span className="font-display text-xl font-light text-[#071526] transition-colors duration-300 group-hover:text-[#184167] md:text-2xl">
                wofersglobal@gmail.com
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-8 flex items-start gap-4">
              <MapPin size={22} weight="thin" className="mt-0.5 shrink-0 text-[#184167]" />
              <span
                data-testid="contact-address"
                className="text-sm font-light leading-relaxed text-[#5C6B7C] md:text-base"
              >
                34 Abashidze Street,
                <br />
                Tbilisi, Georgia
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 flex flex-wrap gap-4">
              {SOCIALS.map((s) => (
                <a
                  key={s.href}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={s.testId}
                  className="inline-flex items-center gap-3 border border-[#E2E8F0] bg-white px-5 py-3 text-xs font-medium uppercase tracking-[0.15em] text-[#071526] shadow-sm transition-colors duration-300 hover:border-[#184167]/60 hover:text-[#184167]"
                >
                  <InstagramLogo size={18} weight="light" className="text-[#184167]" />
                  {s.label} — {s.handle}
                </a>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.45}>
            <a
              href="/wofers-global-partnership-deck.pdf"
              download
              data-testid="download-deck-button"
              className="group mt-8 inline-flex items-center gap-3 border border-[#184167]/60 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#184167] transition-colors duration-300 hover:bg-[#184167] hover:text-white"
            >
              <DownloadSimple size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-y-0.5" />
              Download Partnership Deck
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="border border-[#E2E8F0] bg-white p-7 shadow-sm md:p-10"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name *"
                data-testid="contact-name-input"
                className={INPUT_CLASS}
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Email address *"
                data-testid="contact-email-input"
                className={INPUT_CLASS}
              />
              <input
                value={form.company}
                onChange={set("company")}
                placeholder="Company"
                data-testid="contact-company-input"
                className={INPUT_CLASS}
              />
              <select
                value={form.partner_type}
                onChange={set("partner_type")}
                data-testid="contact-partner-type-select"
                className={`${INPUT_CLASS} appearance-none ${form.partner_type ? "" : "text-[#5C6B7C]/60"}`}
              >
                <option value="" className="bg-white">You are a…</option>
                {PARTNER_OPTIONS.map((o) => (
                  <option key={o} value={o} className="bg-white">
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us about your business and the partnership you have in mind *"
              data-testid="contact-message-input"
              className={`${INPUT_CLASS} mt-5 resize-none`}
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group mt-7 inline-flex w-full items-center justify-center gap-3 bg-[#184167] px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#112E4A] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Enquiry"}
              <PaperPlaneTilt
                size={16}
                weight="bold"
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
